import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "@store/slices/authSlice";
import { Outlet, useNavigate } from "react-router-dom";
import RefreshHandler from "@components/shared/RefreshHandler";
import SideBarCommon from "@components/shared/SideBarCommon";
import { ROLE_ROUTES } from "@utils/constants";
import { logoutUser } from "@store/thunks/authAction";
import TranslatorSidebar from "@components/shared/TranslatorSidebar";
import { addAssignedFiles, addAssignedFolders, addAssignedProjects, setSelectedAssignedFileId, setSelectedAssignedFolderId, setSelectedAssignedProjectId } from "@/store/slices/translatorAdminSlice";

const TranslatorAdminLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { role, user } = useSelector((store) => store.auth);

  const destinationRoute = ROLE_ROUTES[role] || ROLE_ROUTES.DEFAULT;

  const isProjectPage = location.pathname === destinationRoute || location.pathname === `${destinationRoute}/`;
  const isFolderPage = location.pathname === `${destinationRoute}/folders`;
  const isFilePage = location.pathname === `${destinationRoute}/folders/files`;

  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // 👈 state to control sidebar

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev); // 👈 toggle function

  // Determine role-based prefix
  const rolePrefix = role === "TRANSLATOR USER" ? "Translator" : "Translator Admin";

  // Determine header text based on route and role
  const headerTitle = isProjectPage
    ? <span><span className="text-[#ec7026] shadow-sm">{rolePrefix}</span> Dashboard Projects</span>
    : isFolderPage
      ? <span><span className="text-[#ec7026] shadow-sm">{rolePrefix}</span> Dashboard Folders</span>
      : isFilePage
        ? <span><span className="text-[#ec7026] shadow-sm">{rolePrefix}</span> Dashboard Files</span>
        : <span><span className="text-[#ec7026] shadow-sm">{rolePrefix}</span> Dashboard Sentences</span>

  const headerSubtitle = isProjectPage
    ? `Manage your ${rolePrefix} projects and settings`
    : isFolderPage
      ? `Manage your ${rolePrefix} folders and settings`
      : isFilePage
        ? `Manage your ${rolePrefix} files and settings`
        : `Manage your ${rolePrefix} sentences and settings`

  const handleClickBack = () => {
    navigate(-1);
  };
  const handleClickHome = () => {
    navigate(destinationRoute);
    dispatch(addAssignedProjects([])); // Clear assigned projects on home navigation
    dispatch(addAssignedFolders({}));
    dispatch(addAssignedFiles({}));
    dispatch(setSelectedAssignedProjectId(null));
    dispatch(setSelectedAssignedFolderId(null));
    dispatch(setSelectedAssignedFileId(null));
  };


  return (
    <div className="flex min-h-screen bg-gray-50/50">
      {/* ✅ Sidebar - Collapsible */}
      <div
        className={`transition-all duration-300 ${isSidebarOpen ? "w-1/6" : "w-0"
          } overflow-hidden`}
      >
        {role === "TRANSLATOR_USER" ? <TranslatorSidebar /> : <SideBarCommon />}
      </div>

      {/* ✅ Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* ✅ Header */}
        <div
          className={`fixed top-16 transition-all duration-300 bg-white border-b border-gray-200 shadow-sm z-30 ${isSidebarOpen ? "left-[16.666%] w-[83.334%]" : "left-0 w-full"
            }`}
        >
          <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
              <div className="flex flex-row items-start">
                {/* ✅ Hamburger Menu */}
                <button
                  onClick={toggleSidebar}
                  className="p-4 focus:outline-none text-gray-700 hover:text-[#ec7026] transition-colors duration-200"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                </button>

                {/* ✅ Header Title + Subtitle */}
                <div className="flex flex-col">
                  <h1 className="text-2xl font-['Semantic/Strong'] font-bold text-gray-900">
                    {headerTitle}
                  </h1>
                  <p className="text-sm text-gray-500 font-['Semantic/Strong']">
                    {headerSubtitle}
                  </p>
                </div>
              </div>

              {/* ✅ Buttons */}
              <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
                <button
                  onClick={handleClickHome}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                >
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  <span>Home</span>
                </button>

                <button
                  onClick={handleClickBack}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                >
                  <svg
                    className="w-4 h-4 mr-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  <span>Back</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Page Content */}
        <div className="flex-1 w-full overflow-y-auto p-4 sm:p-6 lg:p-8 pt-[144px] mt-28">
          <Outlet />
        </div>
      </div>
    </div>
  );

};

export default TranslatorAdminLayout;
